import React, { useState, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Header from '@cloudscape-design/components/header';
import Table from '@cloudscape-design/components/table';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Box from '@cloudscape-design/components/box';
import Badge from '@cloudscape-design/components/badge';
import Link from '@cloudscape-design/components/link';
import Container from '@cloudscape-design/components/container';
import ColumnLayout from '@cloudscape-design/components/column-layout';
import Tabs from '@cloudscape-design/components/tabs';
import { AiBadge } from '../components/AiBadge';
import { isBedrockEvent } from '../utils/isBedrockEvent';
import { resolveEventType } from '../utils/resolveEventType';
import { describeView, getViewEvents } from '../api/stores';
import { listProjections, Projection } from '../api/projections';
import { FlowTab, SelectedEntity } from './tabs/FlowTab';
import { getCredentials } from '../api/client';
import { formatTimestamp, getStoredTimeFormat } from '../utils/timeFormat';

export function ViewDetail() {
  const { viewName } = useParams<{ viewName: string }>();
  const navigate = useNavigate();
  const location = useLocation();

  // Derive the selected graph entity from the URL path — same pattern as
  // StoreDetail. Entity sub-routes let a schema/rule node be deep-linked and
  // keep the graph info panel open on reload.
  const selectedEntity = useMemo<SelectedEntity | null>(() => {
    const path = location.pathname;
    const viewPrefix = `/views/${viewName}`;
    const suffix = path.slice(viewPrefix.length);

    const schemasMatch = suffix.match(/^\/schemas\/(.+)$/);
    if (schemasMatch) return { type: 'schema', id: decodeURIComponent(schemasMatch[1]) };

    const rulesMatch = suffix.match(/^\/rules\/(.+)$/);
    if (rulesMatch) return { type: 'rule', id: decodeURIComponent(rulesMatch[1]) };

    return null;
  }, [location.pathname, viewName]);

  // Tab state: default to 'graph'. When an entity is deep-linked in the URL the
  // Graph tab is forced active so the info panel is visible.
  const [localTab, setLocalTab] = useState('graph');
  const activeTab = useMemo(() => {
    if (selectedEntity) return 'graph';
    return localTab;
  }, [selectedEntity, localTab]);

  const { data: viewData } = useQuery({
    queryKey: ['view', viewName],
    queryFn: () => describeView(viewName!),
    enabled: !!getCredentials() && !!viewName,
  });

  const { data: eventsData, isLoading, error } = useQuery({
    queryKey: ['viewEvents', viewName],
    queryFn: () => getViewEvents(viewName!, { limit: 100 }),
    enabled: !!getCredentials() && !!viewName,
    refetchInterval: 5000,
  });

  // Prefetch a small page so the Ledger tab is warm before the user clicks it.
  useQuery({
    queryKey: ['viewEvents-prefetch', viewName],
    queryFn: () => getViewEvents(viewName!, { limit: 25 }),
    enabled: !!getCredentials() && !!viewName,
    refetchInterval: 10000,
  });

  const view = (viewData?.data as any) || {};
  const sources: string[] = view.sources || [];

  // View-level projections — the cross-store transaction state that is the
  // unique value of a view. Per-store projections live on each store's page.
  const { data: viewProjectionsData } = useQuery({
    queryKey: ['projections', viewName],
    queryFn: () => listProjections(viewName!),
    enabled: !!getCredentials() && !!viewName,
    refetchInterval: 5000,
  });
  const viewProjections: Projection[] = (viewProjectionsData as any)?.projections || [];

  const responseData = (eventsData?.data as any) || {};
  const events = responseData.events || [];
  const queryError = error as Error | null;

  return (
    <SpaceBetween size="l">
      <Header variant="h1">
        <Badge color="blue">VIEW</Badge>{' '}
        {viewName}
      </Header>

      <Container header={<Header variant="h2">Definition</Header>}>
        <ColumnLayout columns={3}>
          <div>
            <Box variant="awsui-key-label">Sources</Box>
            <Box>
              {sources.length === 0
                ? '-'
                : sources.map((s, i) => (
                    <React.Fragment key={s}>
                      {i > 0 && ', '}
                      <Link href={`/stores/${s}`} onFollow={(e) => { e.preventDefault(); navigate(`/stores/${s}`); }}>
                        {s}
                      </Link>
                    </React.Fragment>
                  ))}
            </Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Correlation Key</Box>
            <Box>{view.correlationKey || '-'}</Box>
          </div>
          <div>
            <Box variant="awsui-key-label">Status</Box>
            <Box>{view.status || '-'}</Box>
          </div>
        </ColumnLayout>
      </Container>

      <Tabs
        activeTabId={activeTab}
        onChange={({ detail }) => {
          setLocalTab(detail.activeTabId);
          // Leaving the Graph tab while an entity is deep-linked clears the
          // entity from the URL so the info panel doesn't re-open.
          if (selectedEntity) {
            navigate(`/views/${viewName}`, { replace: true });
          }
        }}
        tabs={[
          {
            id: 'graph',
            label: 'Graph',
            content: (
              <FlowTab
                storeName={viewName || ''}
                sources={sources}
                selectedEntity={selectedEntity}
                onEntitySelect={(entity) => {
                  if (entity) {
                    const segment = entity.type === 'schema' ? 'schemas' : 'rules';
                    navigate(`/views/${viewName}/${segment}/${encodeURIComponent(entity.id)}`);
                  } else {
                    navigate(`/views/${viewName}`, { replace: true });
                  }
                }}
              />
            ),
          },
          {
            id: 'events',
            label: 'Ledger',
            content: (
              <SpaceBetween size="s">
                {queryError && <Box color="text-status-error">Error: {queryError.message}</Box>}
                <Table
                  loading={isLoading}
                  loadingText="Loading events across sources..."
                  items={events}
                  trackBy="pk"
                  columnDefinitions={[
                    { id: 'source', header: 'Source', cell: (item: any) => (
                      <Link href={`/stores/${item._sourceStore}`} onFollow={(e) => { e.preventDefault(); navigate(`/stores/${item._sourceStore}`); }}>
                        <Badge>{item._sourceStore}</Badge>
                      </Link>
                    ), width: 180 },
                    { id: 'stream', header: 'Stream', cell: (item: any) => { const parts = (item.pk || '').split('#'); return parts.length >= 3 ? parts.slice(2).join('#') : item.pk; } },
                    { id: 'eventType', header: 'Event Type', cell: (item: any) => {
                      // resolveEventType handles the "metadata.eventType='unknown'
                      // + data.sourceEventType fallback" logic once, tested in
                      // src/utils/resolveEventType.test.ts.
                      const resolved = resolveEventType(item);
                      return (
                        <>
                          {resolved}
                          {isBedrockEvent(item) && <AiBadge />}
                        </>
                      );
                    } },
                    { id: 'timestamp', header: 'Timestamp', cell: (item: any) => formatTimestamp(item.eventTimestamp || item.createdAt || '', getStoredTimeFormat()) },
                    { id: 'seq', header: 'Seq', cell: (item: any) => item.sequence ?? '-', width: 60 },
                  ]}
                  empty={<Box textAlign="center">No events. Run demo-traffic to generate data.</Box>}
                />
              </SpaceBetween>
            ),
          },
          {
            id: 'projections',
            label: 'Projections',
            content: (
              <Table
                items={viewProjections}
                trackBy="streamKey"
                columnDefinitions={[
                  { id: 'stream', header: 'Transaction', cell: (item: any) => item.streamKey || '-' },
                  { id: 'payment', header: 'Payment', cell: (item: any) => { const p = item.projectionState?.payment; return p ? `${p.status} $${p.amount} @ ${p.merchant}` : '-'; } },
                  { id: 'risk', header: 'Risk', cell: (item: any) => { const r = item.projectionState?.risk; return r ? `${r.outcome} (score: ${r.score})` : '-'; } },
                  { id: 'settlement', header: 'Settlement', cell: (item: any) => { const s = item.projectionState?.settlement; return s ? `${s.status} $${s.amount}` : '—'; } },
                  { id: 'updated', header: 'Updated', cell: (item: any) => formatTimestamp(item.lastUpdated || '', getStoredTimeFormat()) },
                ]}
                empty={<Box textAlign="center">No view projections. Generate traffic to populate.</Box>}
              />
            ),
          },
        ]}
      />
    </SpaceBetween>
  );
}
